/**
 * Tutor official-documents form section.
 * Criminal-record upload (create vs edit paths), metadata fields, and validate checkbox.
 */
import * as React from 'react';
import { FileText } from 'lucide-react';
import { FileDropzone } from '@/components/common/FileDropzone';
import { Input } from '@/components/forms/Input';
import { DatePicker } from '@/components/forms/DatePicker';
import { Checkbox } from '@/components/forms/Checkbox';
import { TUTOR_CRIMINAL_RECORD_UPLOAD_CONFIG } from '@/components/tutors/tutor-configs';
import { defaultUpload } from '@/components/tutors/remote/tutor-form-utils';
import {
  buildCriminalRecordPatch,
  fetchCriminalRecordSignedUrl,
  isCriminalRecordMetadataComplete,
  postCriminalRecordFile,
  toCriminalRecordUploadInfo,
} from '@/utils/criminal-record-utils';
import type { CriminalRecordUploadInfo } from '@/types/user.types';

/** Props for {@link CriminalRecordField}. */
interface CriminalRecordFieldProps {
  tutorUserId: string;
  existingRecord?: CriminalRecordUploadInfo | null;
  onBatchChange?: (patch: Record<string, any>) => void;
  onUploaded?: (info: CriminalRecordUploadInfo) => void;
  onRemoved?: () => void;
  immediateUploadFn?: (file: File) => Promise<string>;
  onUrlUploaded?: (url: string | null) => void;
  documentLink?: string;
  onFileSelect?: (file: File | null) => void;
}

/** Immediate-upload dropzone for the criminal-record file (create vs edit paths differ). */
function CriminalRecordField({
  tutorUserId,
  existingRecord,
  onBatchChange,
  onUploaded,
  onRemoved,
  immediateUploadFn,
  onUrlUploaded,
  documentLink,
  onFileSelect,
}: Readonly<CriminalRecordFieldProps>) {
  const [uploaded, setUploaded] = React.useState<CriminalRecordUploadInfo | null>(null);
  const [signedUrl, setSignedUrl] = React.useState<string | null>(null);
  /** Latest upload wins over the existing record. */
  const current = uploaded ?? existingRecord ?? null;
  /** Create mode has no user id yet — upload goes through `immediateUploadFn`. */
  const isCreateMode = !tutorUserId && !!immediateUploadFn;

  /** Fetch a signed URL so the dropzone can preview the stored file. */
  const loadSignedUrl = React.useCallback(async () => {
    const url = await fetchCriminalRecordSignedUrl(tutorUserId, documentLink);
    setSignedUrl(url);
  }, [tutorUserId, documentLink]);

  React.useEffect(() => {
    if (tutorUserId && current?.fileName) {
      void loadSignedUrl();
    }
  }, [tutorUserId, current?.fileName, loadSignedUrl]);

  /** Edit-mode upload: POST the file then patch the form. */
  const editUploadFn = React.useCallback(
    async (file: File): Promise<string> => {
      const info = await postCriminalRecordFile(tutorUserId, file);
      setUploaded(info);
      if (onBatchChange) {
        onBatchChange(buildCriminalRecordPatch(info));
      } else {
        onUploaded?.(info);
      }
      void loadSignedUrl();
      return info.fileName;
    },
    [tutorUserId, onBatchChange, onUploaded, loadSignedUrl],
  );

  /** Active upload handler: create uses `immediateUploadFn`, edit uses {@link editUploadFn}. */
  let activeUploadFn: ((file: File) => Promise<string>) | undefined;
  if (isCreateMode) activeUploadFn = immediateUploadFn;
  else if (tutorUserId) activeUploadFn = editUploadFn;

  const handleChange = (url: string) => {
    if (!url) {
      setUploaded(null);
      setSignedUrl(null);
      if (onBatchChange) {
        onBatchChange(buildCriminalRecordPatch(null));
      } else {
        onRemoved?.();
      }
    } else if (isCreateMode) {
      onUrlUploaded?.(url);
    }
  };

  return (
    <FileDropzone
      label="Casier judiciaire (PDF)"
      required
      accept={TUTOR_CRIMINAL_RECORD_UPLOAD_CONFIG.accept}
      maxSize={TUTOR_CRIMINAL_RECORD_UPLOAD_CONFIG.maxSize}
      uploadFn={activeUploadFn}
      onFileSelect={onFileSelect}
      value={current?.fileName ?? undefined}
      previewUrl={signedUrl ?? undefined}
      onChange={handleChange}
      disabled={!activeUploadFn && !onFileSelect}
    />
  );
}

/** Props for {@link TutorOfficialDocumentsSection}. */
type Props = Readonly<{
  formData: Record<string, any>;
  onChange: (path: string, value: any) => void;
  onBatchChange?: (patch: Record<string, any>) => void;
  tutorUserId: string;
  onFileSelect?: (key: string, file: File | null) => void;
}>;

/** Tutor official-documents form section. */
export function TutorOfficialDocumentsSection({
  formData,
  onChange,
  onBatchChange,
  tutorUserId,
  onFileSelect,
}: Props) {
  const criminalRecord = formData.criminalRecord ?? {};
  const allCriminalRecordFilled = isCriminalRecordMetadataComplete(criminalRecord);

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <FileText className="w-4 h-4" />
        Documents officiels
      </h4>
      <div className="space-y-4">
        <CriminalRecordField
          tutorUserId={tutorUserId}
          documentLink={criminalRecord.documentLink ?? undefined}
          immediateUploadFn={!tutorUserId && !onFileSelect ? defaultUpload : undefined}
          onFileSelect={
            !tutorUserId && onFileSelect
              ? (file) => onFileSelect('criminalRecordFile', file)
              : undefined
          }
          onUrlUploaded={
            !tutorUserId && !onFileSelect
              ? (url) => onChange('criminalRecordDocumentLink', url ?? '')
              : undefined
          }
          existingRecord={toCriminalRecordUploadInfo(criminalRecord)}
          onBatchChange={onBatchChange}
          onUploaded={(info) => {
            onChange('criminalRecord.documentName', info.fileName);
            onChange('criminalRecord.uploadDate', info.depositedAt.slice(0, 10));
            onChange('criminalRecord.fileName', info.fileName);
            onChange('criminalRecord.status', info.status);
          }}
          onRemoved={() => {
            onChange('criminalRecord.fileName', '');
            onChange('criminalRecord.documentName', '');
            onChange('criminalRecordDocumentLink', '');
          }}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Numéro de casier"
            value={criminalRecord.recordNumber ?? ''}
            onChange={(e) => onChange('criminalRecord.recordNumber', e.target.value)}
            inputSize="sm"
            placeholder="Ex: 123456789"
          />
          <DatePicker
            label="Date d'émission"
            value={criminalRecord.issueDate ?? ''}
            onChange={(value) => onChange('criminalRecord.issueDate', value)}
            inputSize="sm"
            placeholder="JJ/MM/AAAA"
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 items-end">
          <Input
            label="Délivré par"
            value={criminalRecord.issuedBy ?? ''}
            onChange={(e) => onChange('criminalRecord.issuedBy', e.target.value)}
            inputSize="sm"
            placeholder="Ex: Casier judiciaire national"
          />
          <Checkbox
            label="Engagement sur l'honneur"
            checked={criminalRecord.honorCommitment === true}
            onCheckedChange={(checked) => onChange('criminalRecord.honorCommitment', checked)}
            size="sm"
          />
        </div>
        {tutorUserId && (
          <Checkbox
            label="Marquer comme validé"
            checked={criminalRecord.markValidated === true}
            onCheckedChange={(checked) => onChange('criminalRecord.markValidated', checked)}
            disabled={!allCriminalRecordFilled}
            size="sm"
          />
        )}
      </div>
    </div>
  );
}
